import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Button,
    TextField,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Alert,
    FormControlLabel,
    Switch,
    IconButton,
    Typography
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import BaseDialog from '../common/BaseDialog';
import { businessRoleService, BusinessRole, CreateBusinessRoleDto, UpdateBusinessRoleDto } from '../../services/businessRoleService';

interface RoleFormData {
    name: string;
    isBusinessAdmin: boolean;
    description: string;
}

interface BusinessRolesTabProps {
    businessId: string;
}

const initialFormData: RoleFormData = {
    name: '',
    isBusinessAdmin: false,
    description: ''
};

const BusinessRolesTab: React.FC<BusinessRolesTabProps> = ({ businessId }) => {
    const [roles, setRoles] = useState<BusinessRole[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);
    const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
    const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
    const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
    const [formData, setFormData] = useState<RoleFormData>(initialFormData);
    const [selectedRole, setSelectedRole] = useState<BusinessRole | null>(null);
    const [transferToRoleId, setTransferToRoleId] = useState<number>(0);

    const loadRoles = useCallback(async () => {
        try {
            setIsLoading(true);
            setError(null);
            const response = await businessRoleService.getBusinessRoles(businessId);
            setRoles(response);
        } catch (err) {
            console.error('Error loading roles:', err);
            setError('Failed to load roles. Please try again later.');
        } finally {
            setIsLoading(false);
        }
    }, [businessId]);

    useEffect(() => {
        if (businessId) {
            loadRoles();
        }
    }, [businessId, loadRoles]);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSwitchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setFormData(prev => ({
            ...prev,
            [e.target.name]: e.target.checked
        }));
    };

    const handleCreate = async () => {
        try {
            setIsLoading(true);
            setError(null);
            const createDto: CreateBusinessRoleDto = {
                name: formData.name,
                description: formData.description,
                isBusinessAdmin: formData.isBusinessAdmin
            };
            try {
                await businessRoleService.createBusinessRole(createDto, businessId);
                setSuccess('Role created successfully');
                setIsCreateDialogOpen(false);
                setFormData(initialFormData);
                await loadRoles();
            } catch (error: any) {
                const errorMessage = error.response?.data?.message || error.message || 'Failed to create role';
                setError(errorMessage);
            }
        } catch (error: any) {
            console.error('Error creating role:', error);
            const errorMessage = error.response?.data?.message || error.message || 'An unexpected error occurred';
            setError(errorMessage);
        } finally {
            setIsLoading(false);
        }
    };

    const handleEdit = async () => {
        if (!selectedRole) return;

        try {
            setIsLoading(true);
            setError(null);
            const updateDto: UpdateBusinessRoleDto = {
                name: formData.name,
                description: formData.description,
                isBusinessAdmin: formData.isBusinessAdmin
            };
            try {
                await businessRoleService.updateBusinessRole(selectedRole.id, updateDto, businessId);
                setSuccess('Role updated successfully');
                setIsEditDialogOpen(false);
                setFormData(initialFormData);
                setSelectedRole(null);
                await loadRoles();
            } catch (error: any) {
                const errorMessage = error.response?.data?.message || error.message || 'Failed to update role';
                setError(errorMessage);
            }
        } catch (error: any) {
            console.error('Error updating role:', error);
            const errorMessage = error.response?.data?.message || error.message || 'An unexpected error occurred';
            setError(errorMessage);
        } finally {
            setIsLoading(false);
        }
    };

    const handleDelete = async () => {
        if (!selectedRole) return;

        try {
            setIsLoading(true);
            setError(null);
            try {
                await businessRoleService.deleteBusinessRole(selectedRole.id, businessId);
                setSuccess('Role deleted successfully');
                setIsDeleteDialogOpen(false);
                setSelectedRole(null);
                await loadRoles();
            } catch (error: any) {
                const errorMessage = error.response?.data?.message || error.message || 'Failed to delete role';
                setError(errorMessage);
            }
        } catch (error: any) {
            console.error('Error deleting role:', error);
            const errorMessage = error.response?.data?.message || error.message || 'An unexpected error occurred';
            setError(errorMessage);
        } finally {
            setIsLoading(false);
        }
    };

    const openEditDialog = (role: BusinessRole) => {
        setSelectedRole(role);
        setFormData({
            name: role.name,
            isBusinessAdmin: role.isBusinessAdmin,
            description: role.description || ''
        });
        setIsEditDialogOpen(true);
    };

    const openDeleteDialog = (role: BusinessRole) => {
        setSelectedRole(role);
        setIsDeleteDialogOpen(true);
    };

    return (
        <Box>
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

            <Button
                variant="contained"
                onClick={() => setIsCreateDialogOpen(true)}
                disabled={isLoading}
                sx={{ mb: 2 }}
            >
                Add Role
            </Button>

            <TableContainer component={Paper}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>Name</TableCell>
                            <TableCell>Business Admin</TableCell>
                            <TableCell>Description</TableCell>
                            <TableCell>Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {roles.map((role) => (
                            <TableRow key={role.id}>
                                <TableCell>{role.name}</TableCell>
                                <TableCell>{role.isBusinessAdmin ? 'Yes' : 'No'}</TableCell>
                                <TableCell>{role.description}</TableCell>
                                <TableCell>
                                    <IconButton onClick={() => openEditDialog(role)} disabled={isLoading}>
                                        <EditIcon />
                                    </IconButton>
                                    <IconButton onClick={() => openDeleteDialog(role)} disabled={isLoading}>
                                        <DeleteIcon />
                                    </IconButton>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>

            {/* Create Dialog */}
            <BaseDialog 
                open={isCreateDialogOpen} 
                onClose={() => setIsCreateDialogOpen(false)}
                title="Add New Role"
                actions={
                    <>
                        <Button 
                            onClick={() => setIsCreateDialogOpen(false)}
                            variant="outlined"
                            color="primary">Cancel</Button>
                        <Button onClick={handleCreate} variant="contained" disabled={isLoading}>
                            {isLoading ? 'Creating...' : 'Create'}
                        </Button>
                    </>
                }
            >
                    <Box component="form" sx={{ mt: 2 }}>
                        <TextField
                            fullWidth
                            label="Name"
                            name="name"
                            value={formData.name}
                            onChange={handleInputChange}
                            sx={{ mb: 2 }}
                        />
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={formData.isBusinessAdmin}
                                    onChange={handleSwitchChange}
                                    name="isBusinessAdmin"
                                />
                            }
                            label="Business Admin"
                            sx={{ mb: 2 }}
                        />
                        <TextField
                            fullWidth
                            label="Description"
                            name="description"
                            value={formData.description}
                            onChange={handleInputChange}
                            multiline
                            rows={3}
                        />
                    </Box>
            </BaseDialog>

            {/* Edit Dialog */}
            <BaseDialog 
                open={isEditDialogOpen} 
                onClose={() => setIsEditDialogOpen(false)}
                title="Edit Role"
                actions={
                    <>
                        <Button 
                            onClick={() => setIsEditDialogOpen(false)}
                            variant="outlined"
                            color="primary">Cancel</Button>
                        <Button onClick={handleEdit} variant="contained" disabled={isLoading}>
                            {isLoading ? 'Saving...' : 'Save Changes'}
                        </Button>
                    </>
                }
            >
                    <Box component="form" sx={{ mt: 2 }}>
                        <TextField
                            fullWidth
                            label="Name"
                            name="name"
                            value={formData.name}
                            onChange={handleInputChange}
                            sx={{ mb: 2 }}
                        />
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={formData.isBusinessAdmin}
                                    onChange={handleSwitchChange}
                                    name="isBusinessAdmin"
                                />
                            }
                            label="Business Admin"
                            sx={{ mb: 2 }}
                        />
                        <TextField
                            fullWidth
                            label="Description"
                            name="description"
                            value={formData.description}
                            onChange={handleInputChange}
                            multiline
                            rows={3}
                        />
                    </Box>
            </BaseDialog>

            {/* Delete Dialog */}
            <BaseDialog 
                open={isDeleteDialogOpen} 
                onClose={() => setIsDeleteDialogOpen(false)}
                title="Delete Role"
                actions={
                    <>
                        <Button 
                            onClick={() => setIsDeleteDialogOpen(false)}
                            variant="outlined"
                            color="primary">Cancel</Button>
                        <Button onClick={handleDelete} color="error" variant="contained" disabled={isLoading}>
                            {isLoading ? 'Deleting...' : 'Delete'}
                        </Button>
                    </>
                }
            >
                <Typography>
                    Are you sure you want to delete this role?
                </Typography>
            </BaseDialog>
        </Box>
    );
};

export default BusinessRolesTab; 